import { DollarSign, MapPin, Phone, Mail, Clock, Shield, Users, CheckCircle, Star, Award, CreditCard, Zap, Building, FileText, Calculator, TrendingUp, BookOpen, HelpCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const ResponsibleLending = () => {
  const principles = [
    {
      title: "Transparent Terms",
      description: "All loan terms, fees, and conditions are clearly disclosed upfront with no hidden costs or surprises.",
      icon: <FileText className="w-8 h-8 text-blue-600" />
    },
    {
      title: "Affordability Assessment",
      description: "We evaluate each customer's ability to repay before approving any loan to prevent over-borrowing.",
      icon: <Calculator className="w-8 h-8 text-green-600" />
    },
    {
      title: "Fair Lending Practices",
      description: "We treat all customers fairly regardless of their credit history or financial background.",
      icon: <Shield className="w-8 h-8 text-purple-600" />
    },
    {
      title: "Customer Education",
      description: "We provide resources and guidance to help customers make informed financial decisions.",
      icon: <BookOpen className="w-8 h-8 text-orange-600" />
    }
  ];

  const guidelines = [
    "Only borrow what you can afford to repay",
    "Understand all terms and conditions before accepting a loan",
    "Consider alternatives like family assistance or employer advances",
    "Use short-term loans for emergencies, not regular expenses",
    "Contact us immediately if you're having trouble making payments",
    "Budget carefully to ensure you can meet repayment obligations"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-100">
      {/* Header */}
      <header className="bg-white shadow-lg">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-4">
              <img 
                src="https://quickloanpro.com/storage/2023/11/Payday-Loans-Louisiana-Logo.png" 
                alt="QuickLoanPro Logo"
                className="w-16 h-16 object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">QuickLoanPro</h1>
                <p className="text-gray-600">Your Trusted Financial Partner</p>
              </div>
            </Link>
            <div className="hidden md:flex items-center gap-4">
              <Phone className="w-5 h-5 text-green-600" />
              <span className="text-gray-700">+1 (504) 438-1753</span>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-green-600 text-white py-3">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-6">
            <Link to="/" className="hover:text-green-200 transition-colors">Home</Link>
            <Link to="/payday-loans" className="hover:text-green-200 transition-colors">Payday Loans</Link>
            <Link to="/personal-loans" className="hover:text-green-200 transition-colors">Personal Loans</Link>
            <Link to="/how-it-works" className="hover:text-green-200 transition-colors">How It Works</Link>
            <Link to="/responsible-lending" className="font-semibold">Responsible Lending</Link>
            <Link to="/contact" className="hover:text-green-200 transition-colors">Contact</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-blue-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Responsible Lending Practices
          </h2>
          <p className="text-xl md:text-2xl mb-8 opacity-90 max-w-3xl mx-auto">
            At QuickLoanPro, we're committed to responsible lending that puts our customers' financial well-being first. Learn about our practices and borrowing guidelines.
          </p>
        </div>
      </section>

      {/* Our Commitment */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl font-bold mb-6 text-gray-900">Our Commitment to You</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              QuickLoanPro is dedicated to providing financial services that are fair, transparent, and designed to help rather than harm our customers' financial situations. We believe that responsible lending is not just good business practice—it's the right thing to do.
            </p>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
            Our Responsible Lending Principles
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {principles.map((principle, index) => (
              <Card key={index} className="text-center hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-8">
                  <div className="flex justify-center mb-4">
                    {principle.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-4">{principle.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{principle.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Borrowing Guidelines */}
      <section className="py-16 bg-gradient-to-r from-green-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
              Responsible Borrowing Guidelines
            </h2>
            <Card className="p-8">
              <CardContent>
                <p className="text-lg text-gray-700 mb-6">
                  Before taking out any loan, we encourage you to consider these important guidelines:
                </p>
                <div className="space-y-4">
                  {guidelines.map((guideline, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-green-600 mt-0.5" />
                      <p className="text-gray-700">{guideline}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Regulatory Compliance */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
              Regulatory Compliance
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="p-6">
                <CardContent>
                  <h3 className="text-xl font-semibold mb-4 text-blue-800">Licensed Operations</h3>
                  <p className="text-gray-700 mb-4">
                    QuickLoanPro is a fully licensed financial service provider that complies with all applicable state and federal regulations governing short-term lending.
                  </p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li>• State licensing compliance</li>
                    <li>• Federal regulation adherence</li>
                    <li>• Regular compliance audits</li>
                    <li>• Industry best practices</li>
                  </ul>
                </CardContent>
              </Card>
              
              <Card className="p-6">
                <CardContent>
                  <h3 className="text-xl font-semibold mb-4 text-green-800">Consumer Protection</h3>
                  <p className="text-gray-700 mb-4">
                    We implement strong consumer protection measures to ensure fair treatment and transparent communication with all our customers.
                  </p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li>• Clear disclosure requirements</li>
                    <li>• Fair debt collection practices</li>
                    <li>• Privacy protection measures</li>
                    <li>• Complaint resolution procedures</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Help Resources */}
      <section className="py-16 bg-gradient-to-r from-green-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-8 text-gray-900">
              Need Help or Have Concerns?
            </h2>
            <p className="text-lg text-gray-700 mb-8">
              If you're experiencing financial difficulties or have concerns about your loan, we're here to help. Contact us to discuss your options.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-green-600 hover:bg-green-700 text-white font-bold px-8 py-4"
              >
                <Link to="/contact">Contact Customer Service</Link>
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-green-600 text-green-600 hover:bg-green-50 font-bold px-8 py-4"
              >
                Call +1 (504) 438-1753
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xl font-semibold mb-4">QuickLoanPro</h3>
              <p className="text-gray-400 mb-4">
                Your trusted partner for responsible lending and financial assistance across Louisiana.
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>+1 (504) 438-1753</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span>info@quickloanpro.com</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <span>4901 Bloomfield St, New Orleans, LA 70121</span>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Loan Services</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/payday-loans" className="hover:text-white">Payday Loans</Link></li>
                <li><Link to="/personal-loans" className="hover:text-white">Personal Loans</Link></li>
                <li><Link to="/installment-loans" className="hover:text-white">Installment Loans</Link></li>
                <li><Link to="/cash-advances" className="hover:text-white">Cash Advances</Link></li>
                <li><Link to="/title-loans" className="hover:text-white">Title Loans</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/how-it-works" className="hover:text-white">How It Works</Link></li>
                <li><Link to="/loan-calculator" className="hover:text-white">Loan Calculator</Link></li>
                <li><Link to="/faq" className="hover:text-white">FAQ</Link></li>
                <li><Link to="/responsible-lending" className="hover:text-white">Responsible Lending</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/about" className="hover:text-white">About Us</Link></li>
                <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
                <li><Link to="/privacy" className="hover:text-white">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 QuickLoanPro. All rights reserved.</p>
            <p className="mt-2">Licensed financial service provider committed to responsible lending practices.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ResponsibleLending;